import { useState, useEffect, useMemo } from 'react'
import Button from 'components/button'
import Input from "components/input";
import { Label, SelectGender } from 'theme/theme'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import formServices from 'services/form-builder-services'
import { AxiosError } from 'axios'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { Question } from 'contexts/survey-context/survey.interface'
import ProfileService from 'services/profile-services'
import { TitleSurvey } from '../../../styles/styled.components'

interface IProps {
  title: string
  setInfo: any
  info: any
  changeStep: (count: string) => void
  answers: any
  question: Question
}

const StepOne = ({ title, setInfo, info, changeStep, answers, question }: IProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: formServices.formValidateErrors(
      yupResolver(formServices.formGenerateSchema(['postalCode'])),
      'address',
      'datos cuenta'
    )
  })
  const [data, setData] = useState<any[]>([])

  // I grab user from localstorage because the useSsesion is generating an "infinite loop"
  const ls = localStorage.getItem('state')
  const dataUser = ls ? JSON.parse(ls) : {}

  async function onFormSubmit() {

    dlTrackEvent('user_interaction', {
      action: 'click',
      element: 'Siguiente',
      section: `survey`
    })

    answers.push({
      optionIds: question?.options[0]?.optionId ? [question?.options[0]?.optionId] : [],
      questionId: question?.questionId,
      textValue: `${info?.neighborhoodId}`
    })

    changeStep('increment')
  }

  const [debouncedPostalCode, setDebouncedPostalCode] = useState(info.postalCode)
  const [selectedColonia, setSelectedColonia] = useState(info.neighborhoodId)
  const [error, setError] = useState(null)

  const searchZipCode = async () => {
    try {

      const completeField = info?.postalCode?.length > 4;
      dlTrackEvent('form_interaction', {
        form_field: 'codigo postal',
        form_action: completeField ? 'complete' : 'empty',
        form_name: 'nomina tu cp',
        section: 'survey :: nomina cp'
      })

      if (completeField) {

        toast.loading('Buscando código postal', { position: 'top-center' })
        const response = await ProfileService.checkZip({ postalCode: info?.postalCode }, { session: dataUser?.data })
        toast.dismiss()

        if (response.status === 204) {
          toast.error('Ups! No hemos encontrado el código postal, chequea nuevamente.', { position: 'bottom-center' })
        } else {
          setError(null)
          const infoObject = { ...info, state: response.data[0].name, municipality: response.data[0].cities[0].name }
          if (info?.postalCode !== debouncedPostalCode) {
            infoObject.neighborhoodId = null
            setSelectedColonia(null)
          };

          setValue('city', infoObject.municipality)
          setValue('state', infoObject.state)
          setInfo(infoObject)
          setData(response.data[0].cities[0].neighborhoods)
          setDebouncedPostalCode(info.postalCode)
        }
      } else {
        setData([])
        setInfo({
          ...info,
          neighborhoodId: null,
          municipality: '',
          state: ''
        })
        setValue('city', '')
        setValue('state', '')
        setSelectedColonia('')
      }

    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setError(code)
      toast.dismiss()
      toast.error('Ups! No hemos encontrado el código postal, chequea nuevamente.', { position: 'bottom-center' })
      setData([])
      setInfo({
        ...info,
        state: '',
        neighborhoodId: null,
        municipality: ''
      })
      setSelectedColonia('')
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      searchZipCode()
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [info?.postalCode])

  const isInfoComplete = useMemo(() => {
    return info.neighborhoodId !== null
  }, [info.neighborhoodId])

  return (
    <form className="flex justify-between flex-col flex-1 items-center h-full w-full survey-container" onSubmit={handleSubmit(onFormSubmit)}>
      <div className="w-full px-6 flex-1">
        <div className='flex flex-col justify-center items-center pt-4'>
          <TitleSurvey className="font-black text-4xl text-center title-survey">Encuesta</TitleSurvey>
          <TitleSurvey className="font-black text-4xl text-center title-survey">{title}</TitleSurvey>
          <p className='text big text-center pt-2'>Dínos cuál es tu código postal.</p>
        </div>

        <div className={`mt-6 gap-2 flex flex-col input-container ${(errors?.postalCode?.message || error) ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <Label className="text-start text-2xl font-bold uppercase input-label">CÓDIGO POSTAL</Label>
          </div>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="Código postal"
            name="postalCode"
            {...register("postalCode", {
              required: true,
              maxLength: 8,
              onChange: (e) => {
                setInfo({
                  ...info,
                  postalCode: e.target.value,
                  colonia: ''
                })
              }
            })}
          />
        </div>
        <div className={`mt-5 gap-2 flex flex-col input-container ${errors?.neighborhoodId?.message ? 'error-input' : ''}`}>
          <Label className="text-start text-2xl font-bold uppercase input-label">Tu colonia</Label>
          <SelectGender
            name="neighborhoodId"
            disabled={!data.length}
            className="text-black w-full h-16 din p-4 pr-7 input-select"
            {...register("neighborhoodId", {
              value: selectedColonia,
              onChange: (e) => {
                setInfo({
                  ...info,
                  neighborhoodId: e.target.value
                })
                setSelectedColonia(e.target.value)
              }
            })}
          >
            {data?.map((item: any) => (
              <option value={item.id} key={`${item?.name}-${item?.id}`}>
                {item?.name}
              </option>
            ))}
          </SelectGender>
        </div>
        <div className={`mt-4 gap-2 flex flex-col input-container ${errors?.city?.message ? 'error-input' : ''}`}>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="Municipio"
            name="city"
            {...register("city")}
            disabled
          />
        </div>
        <div className={` mt-4 gap-2 flex flex-col input-container ${errors?.state?.message ? 'error-input' : ''}`}>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="Estado"
            name="state"
            {...register("state")}
            disabled
          />
        </div>
      </div>

      <div className="mt-10 mb-12 flex flex-col" style={{ minWidth: '323px' }}>
        <Button className="w-full" style={{ opacity: isInfoComplete ? 1 : 0.5 }} disabled={!isInfoComplete}>
          Siguiente
        </Button>
      </div>
    </form>
  )
}

export default StepOne
